<template>
  <AppContainer max-width="max-w-3xl">
    <form
      autocomplete="off"
      @submit.prevent="submitForm"
    >
      <AppCard>
        <div>
          <div>
            <AppGrid cols="2">
              <AppGridItem>
                <TextInput
                  v-model.trim="formData.firstName"
                  label="First Name"
                  :class="{ 'input-error': hasValidationError('firstName') }"
                  :validation-error="hasValidationError('firstName') ? getValidationError('firstName') : null"
                  required
                  focus
                />
              </AppGridItem>

              <AppGridItem>
                <TextInput
                  v-model.trim="formData.lastName"
                  label="Last Name"
                  :class="{ 'input-error': hasValidationError('lastName') }"
                  :validation-error="hasValidationError('lastName') ? getValidationError('lastName') : null"
                  required
                />
              </AppGridItem>

              <AppGridItem col-span="2">
                <TextInput
                  v-model.trim="formData.email"
                  label="Email"
                  :class="{ 'input-error': hasValidationError('email') }"
                  class="w-1/2"
                  :validation-error="hasValidationError('email') ? getValidationError('email') : null"
                  required
                  type="email"
                />
              </AppGridItem>

              <AppGridItem
                v-if="isSystemAdministrator"
                col-span="2"
                class="input-field mb-1"
              >
                <label for="roles">
                  Roles
                </label>

                <Multiselect
                  v-model="formData.roles"
                  :options="Object.values(Role)"
                  :loading="loading"
                  multiple
                  :limit="3"
                  :limit-text="count => `+ ${count} more`"
                  :close-on-select="false"
                  :show-labels="false"
                  placeholder="Select roles"
                  :searchable="false"
                  class="user-form-multiselect"
                />

                <span
                  v-if="hasValidationError('roles')"
                  class="text-red-500"
                >
                  {{ getValidationError('roles') }}
                </span>
              </AppGridItem>

              <AppGridItem
                col-span="2"
                class="input-field"
              >
                <label for="municipalities">
                  Municipalities
                </label>

                <Multiselect
                  v-model="selectedMunicipalities"
                  :options="municipalities"
                  track-by="id"
                  label="name"
                  :loading="loading"
                  multiple
                  :limit="2"
                  :limit-text="count => `+ ${count} more`"
                  :close-on-select="false"
                  :show-labels="false"
                  placeholder="Select a municipality"
                  :searchable="false"
                  class="user-form-multiselect"
                />

                <span
                  v-if="hasValidationError('municipalities')"
                  class="text-red-500"
                >
                  {{ getValidationError('municipalities') }}
                </span>
              </AppGridItem>
            </AppGrid>
          </div>
        </div>

        <template #buttons>
          <ButtonSubmitRow
            :cancel-route="isSystemAdministrator ? { name: 'app_user_list' } : planOverviewRoute"
            :processing="isSubmitting"
          />
        </template>
      </AppCard>
    </form>
  </AppContainer>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, reactive, ref, watch } from 'vue';
import Multiselect from 'vue-multiselect';
import { useRouter } from 'vue-router';

import { AppContainer, AppGrid, AppGridItem } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { AppCard } from '@/components/cards';
import { TextInput } from '@/components/form-inputs';

import { useFormSubmit, useHttp, useToasts } from '@/composables';
import { useAuthStore } from '@/store';
import { Role } from '@/const';

const props = defineProps({
  model: {
    type: Object,
    default: () => null,
  },
});

const { submit, hasValidationError, getValidationError, isSubmitting } = useFormSubmit();
const router = useRouter();
const authStore = useAuthStore();

const isSystemAdministrator = computed(() => authStore.user.roles.includes(Role.SystemAdministrator));
const planOverviewRoute = computed(() => ({ name: 'app_plan_overview', params: { municipality: authStore.currentMunicipality?.id } }));

const loading = ref(true);

// A Municipal Administrator can only add IDP Officers, and only to municipalities they belong to,
// so the role is set for them and the municipalities start on their current one, unless it's archived
const municipalities = ref([]);
const startingMunicipalities = isSystemAdministrator.value || authStore.currentMunicipality?.archived ? [] : [authStore.currentMunicipality];
const formData = reactive({
  firstName: props.model?.firstName || null,
  lastName: props.model?.lastName || null,
  email: props.model?.email || null,
  roles: props.model?.roles || (isSystemAdministrator.value ? [] : [Role.IdpOfficer]),
  municipalities: props.model?.municipalities.map(municipality => municipality.id) || startingMunicipalities.map(municipality => municipality.id),
});

const selectedMunicipalities = ref(props.model?.municipalities || startingMunicipalities);

const loadMunicipalities = async () => {
  const response = await useHttp().get('/util/municipalities');

  if (response.statusCode === HttpStatusCode.Ok) {
    municipalities.value = response.data.items;
  }

  loading.value = false;
};

if (isSystemAdministrator.value) {
  loadMunicipalities();
} else {
  municipalities.value = authStore.user.municipalities.filter(municipality => !municipality.archived);
  loading.value = false;
}

const submitForm = async () => {
  // Log the user out when they change their own roles, so the new permissions take effect on login.
  const isOwnProfile = props.model?.id === authStore.user?.id;
  const previousRoles = props.model?.roles || [];

  const rolesChanged = isOwnProfile && (
    formData.roles.length !== previousRoles.length ||
    formData.roles.some(role => !previousRoles.includes(role))
  );

  // Suppress the form's default redirect to the user list when we are about to log the user out,
  // or when the user can't see the list.
  const redirectToList = isSystemAdministrator.value && !rolesChanged;
  const response = await submit(props.model?.id, formData, redirectToList ? 'app_user_list' : null, 'users');

  if (response.statusCode === HttpStatusCode.Ok && !isSystemAdministrator.value) {
    await router.push(planOverviewRoute.value);
    useToasts().success(`An activation email has been sent to ${formData.email}.`);
  }

  if (response.statusCode === HttpStatusCode.Ok && rolesChanged) {
    await useHttp().post('auth/logout');
    authStore.user = null;
    await router.push({ name: 'app_auth_login' });
    useToasts().info('Your permissions have changed. Please log in again.');
  }
};

watch(selectedMunicipalities, () => {
  formData.municipalities = selectedMunicipalities.value.map(municipality => municipality.id);
});
</script>
